/**
 * One reading of HP's FetchParcelDeliveryPoint response.
 *
 * The two lists it returns are shaped differently, and -- the part that matters
 * -- they carry the code HP wants in `sender_pickup_center` /
 * `recipient_delivery_center` in different fields. A parcel locker has an
 * explicit `code`; a post office's identifier *is* its `zip`, which the
 * parameter table spells out as "post office zip code and identifier". Sending
 * the wrong one produces a label HP rejects, so that mapping lives here and
 * both the Delivery Points page and the picker read it from this one place.
 *
 * Working hours come back on post offices too, but the documentation lost the
 * values of the `closed` flag ("- PO closed / - PO open"), so they are left
 * alone rather than guessed at: wrong opening hours are worse than none.
 */

import { api } from './api';

/** `facilityType` values FetchParcelDeliveryPoint accepts. */
export const KIND = {
  POST_OFFICE: 'PU',
  LOCKER: 'PAK',
  ALL: 'ALL',
};

/** The kind of centre each delivery_type / pickup_type value points at. */
export function kindForCentreType(type) {
  return Number(type) === 3 ? KIND.LOCKER : KIND.POST_OFFICE;
}

export function kindLabel(kind) {
  return kind === KIND.LOCKER ? 'parcel locker' : 'post office';
}

const text = (value) => String(value ?? '').trim();

function fromPostOffice(pu) {
  return {
    kind: KIND.POST_OFFICE,
    code: text(pu.zip),
    name: text(pu.name),
    address: text(pu.address),
    zip: text(pu.zip),
    city: text(pu.city),
    tel: text(pu.tel) || null,
  };
}

function fromLocker(pak) {
  return {
    kind: KIND.LOCKER,
    code: text(pak.code),
    name: text(pak.name),
    address: text(pak.address),
    zip: text(pak.zip),
    city: text(pak.city),
    tel: null,
  };
}

/**
 * Flattens a FetchParcelDeliveryPoint payload into one uniform list. `kind`
 * selects a single list; KIND.ALL returns lockers first, matching the order the
 * Delivery Points page shows them in.
 */
export function normalizePoints(data, kind = KIND.ALL) {
  const lockers = (data?.paketomatInfoList || []).map(fromLocker);
  const postOffices = (data?.postaInfoList || []).map(fromPostOffice);

  if (kind === KIND.LOCKER) return lockers;
  if (kind === KIND.POST_OFFICE) return postOffices;
  return [...lockers, ...postOffices];
}

/**
 * Narrows an already-fetched list as the merchant types.
 *
 * HP filters server-side as well, but going back to them on every keystroke
 * would mean an authenticated round trip per character. The whole list for one
 * facility type is a single call, so it is fetched once and sifted here.
 *
 * Terms are matched independently, so "zagreb ilica" finds what neither word
 * alone would order correctly.
 */
export function filterPoints(points, query) {
  const terms = String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (terms.length === 0) return points;

  return points.filter((point) => {
    const haystack = `${point.code} ${point.name} ${point.address} ${point.zip} ${point.city}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

/**
 * HP's network changes rarely and the full list is one sizeable response, so it
 * is held for the life of the page rather than re-fetched every time a picker
 * opens. `clearPointCache` exists because the TEST and PROD environments return
 * different networks -- see the call in Settings after a save.
 */
const cache = new Map();

export async function loadPoints(kind) {
  const cached = cache.get(kind);
  if (cached) return cached;

  const points = normalizePoints(await api.getDeliveryPoints(kind, ''), kind);
  cache.set(kind, points);
  return points;
}

export function clearPointCache(kind) {
  if (kind) cache.delete(kind);
  else cache.clear();
}
